import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirm, useToast } from '@/ui';
import {
  apiErrorName,
  getChannelIntakeDetail,
  getPlatformQuotes,
  initiateChannelPayin,
} from '@/lib/recepthor-api';
import { formatCLP } from '@/lib';
import { qk } from '@/lib/queryKeys';
import { fetchAllPages } from '@/hooks/queries';
import { payinFailure } from './channelPayinCopy';

// Charge of a channel pedido: reads the intake detail (REC-2041) and the quotes
// of its live diligence, and starts the charge over the chosen OFFERED quote.
// Read-only otherwise: the state comes from `liveTask.charge`, and the POST is
// never called again to refresh (a lapsed session would open a new one).
export function useChannelPayin({ intakeId, sender, organizationId, enabled }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const detailKey = qk.channelIntakeQueue.detail(intakeId, organizationId);
  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => getChannelIntakeDetail(intakeId, { organizationId }),
    enabled: enabled && Boolean(intakeId),
  });
  const detail = detailQ.data ?? null;
  const taskId = detail?.liveTask?.taskId ?? null;

  const quotesQ = useQuery({
    queryKey: qk.channelIntakeQueue.taskQuotes(taskId, organizationId),
    // A diligence sent to every receptor of a court outgrows one page, and an
    // offer past it would read as "no offers".
    queryFn: () => fetchAllPages(getPlatformQuotes, { taskId, organizationId }),
    enabled: enabled && Boolean(taskId),
  });
  const quotes = quotesQ.data?.results ?? [];
  const offers = quotes.filter((q) => q.status === 'OFFERED');

  const [pickedId, setPickedId] = useState(null);
  // What the POST answered, tagged with when the GET had last answered by
  // then: it stands in until the next GET that answers, and survives one that
  // fails (the POST may have sent the link by WhatsApp already).
  const [result, setResult] = useState(null);
  const [paidAt, setPaidAt] = useState(null);
  const detailUpdatedAt = detailQ.dataUpdatedAt;
  // Bumped when a start succeeds, so the view can move focus to the result.
  const [startedCount, setStartedCount] = useState(0);
  // Sync guard: two clicks before the confirm dialog or the mutation paint.
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const selectedId = offers.some((q) => q.id === pickedId)
    ? pickedId
    : offers.length === 1
      ? offers[0].id
      : null;

  const mutation = useMutation({
    mutationFn: (quoteId) => initiateChannelPayin(quoteId, { organizationId }),
    onSuccess: (data) => {
      setResult({
        data,
        at: detailUpdatedAt,
      });
      setStartedCount((n) => n + 1);
      toast({
        kind: 'success',
        message:
          data?.chargeCreated === false ? 'Cobro retomado.' : 'Cobro iniciado.',
      });
      // The GET rules once it answers again (a failed charge comes back as
      // `charge: null`).
      queryClient.invalidateQueries({ queryKey: qk.channelIntakeQueue.all() });
    },
    onError: (e) => {
      const failure = payinFailure(e, apiErrorName(e?.body));
      if (failure.paid) {
        setPaidAt(detailUpdatedAt);
        queryClient.invalidateQueries({
          queryKey: qk.channelIntakeQueue.all(),
        });
        return;
      }
      toast({ kind: 'err', ...failure });
    },
  });

  const start = useCallback(async () => {
    const quote = offers.find((q) => q.id === selectedId);
    if (!quote || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const amount = formatCLP(Number(quote.price));
      const ok = await confirm({
        title: 'Iniciar cobro',
        body: (
          <>
            Vas a cobrar la oferta del receptor{' '}
            {amount ? <b>{amount}</b> : null} (más la comisión de servicio)
            {quote.receptor?.name ? <> de {quote.receptor.name}</> : null}. Esto
            le envía el link de pago por WhatsApp a{' '}
            <b>{sender || 'el solicitante'}</b>, salvo que ya tenga uno vigente.
          </>
        ),
        confirmLabel: 'Iniciar cobro',
        confirmVariant: 'seal',
      });
      if (!ok) return;
      await mutation.mutateAsync(quote.id).catch(() => {});
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [confirm, mutation, offers, selectedId, sender]);

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: qk.channelIntakeQueue.all() }),
    [queryClient],
  );

  // One charge view from the GET. What the POST answered only stands in until
  // a GET answers after it; from then on the GET rules (a failed charge comes
  // back as `charge: null`).
  const postResult =
    result && result.at === detailUpdatedAt ? result.data : null;
  const paidFromPost = paidAt !== null && paidAt === detailUpdatedAt;
  const charge = detail?.liveTask?.charge ?? null;
  const session = charge?.checkoutSession ?? null;
  const url = postResult?.url ?? session?.url ?? null;
  const state = postResult?.state ?? session?.state ?? null;
  const chargeQuoteId = charge?.quoteId ?? postResult?.quoteId ?? null;
  const chargeQuote = quotes.find((q) => q.id === chargeQuoteId) ?? null;
  const paid = paidFromPost || charge?.status === 'SUCCESS';
  const hasCharge = Boolean(charge || postResult);

  return {
    loading: detailQ.isPending && detailQ.fetchStatus !== 'idle',
    // Only a GET that never answered is a load error: a refetch that fails
    // keeps the cached detail (and what the POST answered) on screen.
    error: detailQ.isError && !detailQ.data ? detailQ.error : null,
    refreshing: detailQ.isFetching || quotesQ.isFetching,
    detail,
    linkedCase: detail?.linkedCase ?? null,
    liveTask: detail?.liveTask ?? null,
    quotesError: quotesQ.isError,
    startedCount,
    quotesLoading: quotesQ.isPending && quotesQ.fetchStatus !== 'idle',
    offers,
    selectedId,
    onPick: setPickedId,
    busy: busy || mutation.isPending,
    onStart: start,
    onRefresh: refresh,
    charge:
      hasCharge || paid
        ? {
            paid,
            url,
            state,
            resumed: postResult?.chargeCreated === false,
            amount: chargeQuote ? formatCLP(Number(chargeQuote.price)) : null,
          }
        : null,
  };
}
